import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import userApi from '~/apis/user';
import { getProfile } from '~/thunks/auth/authThunk';
import { selectUser } from '~/thunks/auth/authSlice';
import { getErrorMessage } from '~/utils/helper/error';
import { toMultipart } from '~/utils/helper/setupForm';
import { useFilePreview } from '~/utils/hooks/useFilePreview';
import { IAccountProfile } from '~/utils/interface/auth';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-60';

// The signed-in user's own account: name and photo (the senior's profile is a separate page).
const MyProfile = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser) as IAccountProfile | undefined;

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [avatar, setAvatar] = useState<File>();
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string }>();

  // follow the stored profile once it is (re)loaded, e.g. right after a save
  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setAvatar(undefined);
  }, [user?.firstName, user?.lastName, user?.avatarImage]);

  const avatarSrc = useFilePreview(avatar ?? user?.avatarImage ?? undefined);
  const isDirty =
    firstName.trim() !== (user?.firstName ?? '') || lastName.trim() !== (user?.lastName ?? '') || !!avatar;
  const missing = !firstName.trim() || !lastName.trim();

  const handleSave = async () => {
    if (missing) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    setFeedback(undefined);
    try {
      await userApi.updateProfile(
        toMultipart({ firstName: firstName.trim(), lastName: lastName.trim(), avatarImage: avatar })
      );
      await dispatch(getProfile()).unwrap();
      setFeedback({ type: 'success', message: t('pages.account.saved') });
    } catch (error) {
      const reason = getErrorMessage(error);
      setFeedback({ type: 'error', message: reason ?? t('pages.account.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const nameField = (label: string, value: string, onChange: (value: string) => void) => (
    <label className='flex flex-col gap-1 text-sm'>
      <span>{label} *</span>
      <input
        className={`${inputClass} ${showErrors && !value.trim() ? 'border-destructive' : ''}`}
        value={value}
        onChange={(e) => {
          setFeedback(undefined);
          onChange(e.target.value);
        }}
      />
      {showErrors && !value.trim() && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
    </label>
  );

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='min-w-0 max-w-3xl'>
          <h1 className='text-xl font-semibold text-foreground'>{t('pages.account.title')}</h1>
          <p className='text-sm text-muted-foreground'>{t('pages.account.subtitle')}</p>
        </div>
        <button
          type='button'
          onClick={handleSave}
          disabled={!isDirty || saving}
          className='ml-auto rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
        >
          {saving ? t('pages.account.saving') : t('pages.account.save')}
        </button>
      </div>

      <div className='mx-auto flex w-full max-w-3xl flex-col gap-4'>
        {feedback && (
          <p className={`text-sm ${feedback.type === 'success' ? 'text-foreground' : 'text-destructive'}`} role='status'>
            {feedback.message}
          </p>
        )}

        <section className='flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-5'>
          <div className='flex items-center gap-4'>
            <div className='h-16 w-16 shrink-0 overflow-hidden rounded-full bg-muted'>
              {avatarSrc && <img src={avatarSrc} alt='' className='h-full w-full object-cover' />}
            </div>
            <input
              type='file'
              accept='image/*'
              aria-label={t('pages.setup.fields.avatar')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setFeedback(undefined);
                  setAvatar(file);
                }
              }}
            />
          </div>
          {nameField(t('pages.setup.fields.firstName'), firstName, setFirstName)}
          {nameField(t('pages.setup.fields.lastName'), lastName, setLastName)}
          <label className='flex flex-col gap-1 text-sm'>
            <span>{t('pages.setup.fields.username')}</span>
            <input className={inputClass} value={user?.username ?? ''} disabled readOnly />
          </label>
          <label className='flex flex-col gap-1 text-sm'>
            <span>{t('pages.setup.fields.email')}</span>
            <input className={inputClass} value={user?.email ?? ''} disabled readOnly />
          </label>
        </section>
      </div>
    </div>
  );
};

export default MyProfile;
